import ErrorBoundary from "@components/error-boundary";
import type { InitPayloadType, PdfCtxState } from "@config";
import { getPdfDataOnSetup, PdfContextProvider } from "@library/components";
import { store } from "@store/store";
import React, { lazy, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { normalizeToNestedData, retryImport } from "@/shared";
import { ViewProvider } from "../providers";

const Story = lazy(() =>
  retryImport(() => import("../modules/static/components/Story")),
);

const rootsCache = new WeakMap<HTMLElement, ReactDOM.Root>();

type RenderAppProps = {
  root: ReactDOM.Root;
  story: InitPayloadType;
  pdf: PdfCtxState;
  view?: "pdf" | "default";
};

export const useInitStaticStory = () => {
  useEffect(() => {
    const renderRoot = (e: Event) => {
      const details = (e as CustomEvent).detail;
      var match = window.location.href.match(/\d+|templates|wizard/);
      if (window.location.href.indexOf("/pages/editor/") > -1 && !match) return;
      if (!details.element) return;

      const element = details.element;
      if (!element) return;
      const { story, pdf } = setupOnInit(e);
      const view = details.view;
      if (!story) return;

      let root = rootsCache.get(element);
      if (!root) {
        root = ReactDOM.createRoot(element);
        rootsCache.set(element, root);
      }

      renderApp({ root, story, pdf, view });
    };
    document.addEventListener("storydocRender", renderRoot);
    return () => {
      document.removeEventListener("storydocRender", renderRoot);
    };
  }, []);
};

const renderApp = ({ root, story, pdf, view }: RenderAppProps) => {
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <ErrorBoundary
          skipFallback
          feature="Static story view"
          toast="Failed to load story. Please try again later."
        >
          <PdfContextProvider data={pdf}>
            <ViewProvider value={{ view }}>
              <Suspense>
                <Story story={story} />
              </Suspense>
            </ViewProvider>
          </PdfContextProvider>
        </ErrorBoundary>
      </Provider>
    </React.StrictMode>,
  );
};

const setupOnInit = (event?: Event) => {
  const pdf = getPdfDataOnSetup(event);
  const story = getStory(event);

  if (pdf.isPdf) {
    document.body.classList.add("printToPdf");
    document.body.setAttribute("data-sd-print-mode", pdf.printMode);
  }
  return { story, pdf };
};

const getStory = (event?: Event) => {
  const detail = (event as CustomEvent)?.detail || {};
  const { story } = detail;
  if (!story || !story.slides?.[0]) return;
  const slides = story.slides[0].id
    ? story.slides
    : normalizeToNestedData(story.slides);
  return { ...story, slides };
};
